'use client';

import type { DefaultOptions, QueryClient } from '@tanstack/react-query';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { PropsWithChildren } from 'react';

import type { AppRouter } from '~/landing/domains/router';

import { useToast } from '@polyseme/design-system';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { httpBatchLink, httpLink, splitLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { useState } from 'react';
import superjson from 'superjson';

import { createQueryClient } from './query-client';

export type RouterInput = inferRouterInputs<AppRouter>;
export type RouterOutput = inferRouterOutputs<AppRouter>;

export const useGetErrorMessage = () => {
  return (error: Error) => error.message;
};

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = (options?: DefaultOptions) => {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return createQueryClient(options);
  } else {
    // Browser: use singleton pattern to keep the same query client
    return (clientQueryClientSingleton ??= createQueryClient(options));
  }
};

const getUrl = () => {
  const base = (() => {
    if (typeof window !== 'undefined') return '';
    // eslint-disable-next-line no-process-env
    if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
    return 'http://localhost:3080';
  })();
  return `${base}/api/trpc`;
};

export const api = createTRPCReact<AppRouter>();

export const TRPCReactProvider = ({ children }: PropsWithChildren) => {
  const { toast } = useToast();
  const getErrorMessage = useGetErrorMessage();

  const queryClient = getQueryClient({
    mutations: {
      onError: (error) => {
        toast({
          variant: 'destructive',
          title: getErrorMessage(error),
        });
      },
    },
  });

  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        splitLink({
          condition: (op) => {
            return Boolean(op.context?.skipBatch);
          },
          true: httpLink({
            url: getUrl(),
            transformer: superjson,
          }),
          false: httpBatchLink({
            url: getUrl(),
            transformer: superjson,
          }),
        }),
      ],
    })
  );

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        {children}
      </QueryClientProvider>
    </api.Provider>
  );
};
